import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  loadConversationLogs,
  postToCallRegistryLambda,
  startConverstion,
} from '../libs/api/lambdas';

const AppStoreContext = createContext(null);

const POLL_INTERVAL_MS = 10000;
const LOGGED_IN_TRUE = '__q_bool|1';
const LOGGED_IN_FALSE = '__q_bool|0';
const UNAVAILABLE_MESSAGES = [
  'The person you are trying to reach is not available',
  'The number you have reached is not in service',
  'Please leave a message after the tone',
  'The mailbox is full and cannot accept',
  'voicemail',
  'mailbox',
];

const getStoredLoggedInValue = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem('loggedIn') === LOGGED_IN_TRUE;
};

const createDefaultUserLog = (userId) => ({
  userId,
  status: 'not_started',
  logs: [],
  interval: null,
  callId: null,
  details: 'unknown',
});

const mapTranscriptToLogEntry = (transcript) => ({
  date: transcript.created_at,
  message: transcript.text,
  type: transcript.user === 'user' ? 'user' : 'agent',
  transcript_id: String(transcript.id),
});

export const AppStoreProvider = ({children}) => {
  const [isAdvancingToNextCall, setIsAdvancingToNextCall] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUserState] = useState(null);
  const [userCallLogs, setUserCallLogs] = useState([]);
  const [isAutoCallPaused, setIsAutoCallPausedState] = useState(false);
  const [loggedIn, setLoggedInState] = useState(getStoredLoggedInValue);

  const usersRef = useRef(users);
  const selectedUserRef = useRef(selectedUser);
  const userCallLogsRef = useRef(userCallLogs);
  const isAutoCallPausedRef = useRef(isAutoCallPaused);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    userCallLogsRef.current = userCallLogs;
  }, [userCallLogs]);

  useEffect(() => {
    isAutoCallPausedRef.current = isAutoCallPaused;
  }, [isAutoCallPaused]);

  const stopPollingForUser = useCallback((userId) => {
    const currentUserLog = userCallLogsRef.current.find((log) => log.userId === userId);

    if (!currentUserLog || currentUserLog.interval == null) {
      return;
    }

    window.clearInterval(currentUserLog.interval);
    setUserCallLogs((currentLogs) => currentLogs.map((log) => (
      log.userId === userId ? {...log, interval: null} : log
    )));
  }, []);

  const selectFirstUser = useCallback(() => {
    const firstUser = usersRef.current[0] || null;
    setSelectedUserState(firstUser);
  }, []);

  const setLoggedIn = useCallback((value) => {
    setLoggedInState(value);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('loggedIn', value ? LOGGED_IN_TRUE : LOGGED_IN_FALSE);
    }
  }, []);

  const setAutoCallPaused = useCallback((status) => {
    setIsAutoCallPausedState(status);
  }, []);

  const setSelectedUser = useCallback((userId) => {
    const nextUser = usersRef.current.find((user) => user.user_id === userId) || null;
    setSelectedUserState(nextUser);
  }, []);

  const addUser = useCallback((user) => {
    setUsers((currentUsers) => {
      const nextUsers = [...currentUsers, user];

      if (currentUsers.length === 0) {
        setSelectedUserState(user);
      }

      return nextUsers;
    });

    setUserCallLogs((currentLogs) => [...currentLogs, createDefaultUserLog(user.user_id)]);
  }, []);

  const updateUser = useCallback((user) => {
    setUsers((currentUsers) => currentUsers.map((currentUser) => (
      currentUser.user_id === user.user_id ? user : currentUser
    )));

    if (selectedUserRef.current?.user_id === user.user_id) {
      setSelectedUserState(user);
    }
  }, []);

  const removeUser = useCallback((userId) => {
    stopPollingForUser(userId);

    const remainingUsers = usersRef.current.filter((user) => user.user_id !== userId);

    setUsers(remainingUsers);
    setUserCallLogs((currentLogs) => currentLogs.filter((log) => log.userId !== userId));

    if (selectedUserRef.current?.user_id === userId) {
      setSelectedUserState(remainingUsers[0] || null);
    }
  }, [stopPollingForUser]);

  const moveSelectedUserToFirst = useCallback(() => {
    const activeUser = selectedUserRef.current;

    if (!activeUser) {
      return;
    }

    setUsers((currentUsers) => {
      const userIndex = currentUsers.findIndex((user) => user.user_id === activeUser.user_id);

      if (userIndex <= 0) {
        return currentUsers;
      }

      const nextUsers = [...currentUsers];
      const [user] = nextUsers.splice(userIndex, 1);

      if (user) {
        nextUsers.unshift(user);
      }

      return nextUsers;
    });
  }, []);

  const appendLogEntry = useCallback((userId, entry) => {
    setUserCallLogs((currentLogs) => currentLogs.map((log) => (
      log.userId === userId
        ? {...log, logs: [...log.logs, entry]}
        : log
    )));
  }, []);

  const checkUserLogs = useCallback(async (userId) => {

    console.log('inside appstore checkUserLogs');
    console.log('userId', userId);

    const currentUserLog = userCallLogsRef.current.find((log) => log.userId === userId);

    console.log('currentUserLog', currentUserLog);

    if (!currentUserLog || !currentUserLog.callId) {
      return;
    }

    const callStatus = await loadConversationLogs(currentUserLog.callId);

    console.log('callStatus', callStatus);

    if (!callStatus) {
      return;
    }

    let unavailableDetected = false;

    for (const transcript of callStatus.transcripts || []) {
      unavailableDetected = unavailableDetected || (
        typeof transcript.text === 'string' &&
        UNAVAILABLE_MESSAGES.some((message) => transcript.text.includes(message))
      );
    }

    setUserCallLogs((currentLogs) => currentLogs.map((log) => {
      if (log.userId !== userId) {
        return log;
      }

      const transcriptIds = new Set(
        log.logs
          .map((entry) => entry.transcript_id)
          .filter(Boolean)
      );

      console.log('transcriptIds', transcriptIds);

      const nextTranscriptLogs = (callStatus.transcripts || [])
        .filter((transcript) => !transcriptIds.has(String(transcript.id)))
        .map(mapTranscriptToLogEntry);

      console.log('nextTranscriptLogs', nextTranscriptLogs);

      let nextStatus = callStatus.status || 'not_started';
      let nextDetails = callStatus.status_detail || 'unknown';

      if (
        typeof callStatus.answered_by === 'string' &&
        callStatus.answered_by.toLowerCase().includes('voicemail')
      ) {
        nextStatus = 'completed';
        nextDetails = 'voicemailed';
      } else if (
        typeof callStatus.concatenated_transcript === 'string' &&
        callStatus.concatenated_transcript.toLowerCase().includes('not available')
      ) {
        nextStatus = 'completed';
        nextDetails = 'no-answer';
      }

      return {
        ...log,
        logs: [...log.logs, ...nextTranscriptLogs],
        status: nextStatus,
        details: nextDetails,
      };
    }));

    if (unavailableDetected) {
      console.log('unavailableDetected');

      const user = usersRef.current.find((currentUser) => currentUser.user_id === userId);

      if (user) {
        await postToCallRegistryLambda({
          userId: user.user_id,
          firstName: user.first_name,
          lastName: user.last_name,
          answer: '',
          callSequence: currentUserLog.callId || '',
          status: 'noAnswer',
          phone: user.phone_number,
          jobId: user.job_id || '',
          principal: user.principal || '',
          homeOwner: true,
        });
      }
    } else {
      const statusCheck = ['busy', 'no-answer', 'voicemailed', 'failed'];
      const statusValue = String(callStatus.status || '').toLowerCase();
      const detailValue = String(callStatus.status_detail || '').toLowerCase();

      if (statusCheck.includes(statusValue) || statusCheck.includes(detailValue)) {
        const user = usersRef.current.find((currentUser) => currentUser.user_id === userId);
        let lambdaStatus = 'other';

        if (detailValue === 'busy' || statusValue === 'busy') {
          lambdaStatus = 'hangup';
        } else if (detailValue === 'no-answer' || statusValue === 'no-answer') {
          lambdaStatus = 'noAnswer';
        } else if (detailValue === 'voicemailed' || statusValue === 'voicemailed') {
          lambdaStatus = 'voicemailed';
        } else {
          lambdaStatus = callStatus.status || callStatus.status_detail || 'other';
        }

        if (user) {
          await postToCallRegistryLambda({
            userId: user.user_id,
            firstName: user.first_name,
            lastName: user.last_name,
            answer: '',
            callSequence: currentUserLog.callId || '',
            status: lambdaStatus,
            phone: user.phone_number,
            jobId: user.job_id || '',
            principal: user.principal || '',
            homeOwner: true,
          });
        }
      }
    }

    console.log('before completed or waiting_for_human callStatus.status', callStatus.status);

    if (['completed', 'waiting_for_human'].includes(callStatus.status || '')) {
      appendLogEntry(userId, {
        date: new Date().toLocaleString(),
        message: callStatus.status === 'completed' ? 'Call completed' : 'Call waiting for human...',
        type: callStatus.status === 'completed' ? 'completed' : 'debug',
        transcript_id: null,
      });

      stopPollingForUser(userId);

      if (callStatus.status === 'completed') {
        const userIndex = usersRef.current.findIndex((user) => user.user_id === userId);
        const nextUser = usersRef.current[userIndex + 1];

        if (nextUser) {
          setSelectedUserState(nextUser);

          if (!isAutoCallPausedRef.current) {
            setIsAdvancingToNextCall(true);
            void (async () => {
              try {
                await startCall(nextUser.user_id);
              } finally {
                setIsAdvancingToNextCall(false);
              }
            })();
          }
        }
      }
    }
  }, [appendLogEntry, stopPollingForUser]);

  const startCall = useCallback(async (userId) => {

    console.log('appstore startCall');
    console.log('userId', userId);

    const user = usersRef.current.find((currentUser) => currentUser.user_id === userId);
    const currentUserLog = userCallLogsRef.current.find((log) => log.userId === userId);

    console.log('user', user);
    console.log('currentUserLog', currentUserLog);

    if (!user || !currentUserLog) {
      return;
    }

    if (currentUserLog.status === 'in_progress' && currentUserLog.interval != null) {
      console.warn('Call already in progress for user:', userId);
      return;
    }

    stopPollingForUser(userId);

    setUserCallLogs((currentLogs) => currentLogs.map((log) => (
      log.userId === userId
        ? {
          ...log,
          logs: [],
          status: 'in_progress',
          callId: null,
          details: 'unknown',
        }
        : log
    )));

    appendLogEntry(userId, {
      date: new Date().toLocaleString(),
      message: `Initiating call to ${user.first_name} ${user.last_name}...`,
      type: 'debug',
      transcript_id: null,
    });

    const response = await startConverstion(user);

    console.log('response from startConverstion');
    console.log(response);

    if (!response || !response.call_seq) {
      console.error('Failed to start conversation for user:', userId);

      setUserCallLogs((currentLogs) => currentLogs.map((log) => (
        log.userId === userId
          ? {...log, status: 'not_started'}
          : log
      )));

      appendLogEntry(userId, {
        date: new Date().toLocaleString(),
        message: 'Failed to start call.',
        type: 'debug',
        transcript_id: null,
      });
      return;
    }

    setUserCallLogs((currentLogs) => currentLogs.map((log) => (
      log.userId === userId
        ? {...log, callId: response.call_seq}
        : log
    )));

    appendLogEntry(userId, {
      date: new Date().toLocaleString(),
      message: `Dialing ${user.phone_number}...`,
      type: 'debug',
      transcript_id: null,
    });

    if (isAutoCallPausedRef.current) {
      setIsAutoCallPausedState(false);
    }

    console.log('appstore startCall polling');

    let polling = false;
    const interval = window.setInterval(async () => {
      if (polling) {
        return;
      }

      polling = true;

      try {
        await checkUserLogs(userId);
      } finally {
        polling = false;
      }
    }, POLL_INTERVAL_MS);

    setUserCallLogs((currentLogs) => currentLogs.map((log) => (
      log.userId === userId
        ? {...log, interval}
        : log
    )));
  }, [appendLogEntry, checkUserLogs, stopPollingForUser]);

  useEffect(() => () => {
    userCallLogsRef.current.forEach((log) => {
      if (log.interval != null) {
        window.clearInterval(log.interval);
      }
    });
  }, []);

  const getCurrentUserLogs = useMemo(() => (
    userCallLogs.find((log) => log.userId === selectedUser?.user_id)?.logs || []
  ), [selectedUser, userCallLogs]);

  const getCurrentUserCallStatus = useMemo(() => (
    userCallLogs.find((log) => log.userId === selectedUser?.user_id)?.status || 'not_started'
  ), [selectedUser, userCallLogs]);

  const getCurrentUserCallStatusDetails = useMemo(() => (
    userCallLogs.find((log) => log.userId === selectedUser?.user_id)?.details || 'unknown'
  ), [selectedUser, userCallLogs]);

  const getUsersWithCallStatus = useMemo(() => users.map((user) => {
    const userLog = userCallLogs.find((log) => log.userId === user.user_id);

    return {
      ...user,
      status: userLog?.status || 'not_started',
      details: userLog?.details || 'unknown',
    };
  }), [userCallLogs, users]);

  const isCallInProgress = useMemo(() => (
    userCallLogs.some((log) => log.status === 'in_progress') || isAdvancingToNextCall
  ), [isAdvancingToNextCall, userCallLogs]);

  const value = useMemo(() => ({
    users,
    selectedUser,
    getCurrentUserLogs,
    getCurrentUserCallStatus,
    getCurrentUserCallStatusDetails,
    getUsersWithCallStatus,
    addUser,
    updateUser,
    removeUser,
    startCall,
    isCallInProgress,
    isAutoCallPaused,
    setAutoCallPaused,
    isAdvancingToNextCall,
    isUserLoggedIn: loggedIn,
    loggedIn,
    setLoggedIn,
    moveSelectedUserToFirst,
    setSelectedUser,
    selectFirstUser,
    userCallLogs,
  }), [
    addUser,
    getCurrentUserCallStatus,
    getCurrentUserCallStatusDetails,
    getCurrentUserLogs,
    getUsersWithCallStatus,
    isAdvancingToNextCall,
    isAutoCallPaused,
    isCallInProgress,
    loggedIn,
    moveSelectedUserToFirst,
    removeUser,
    selectFirstUser,
    selectedUser,
    setAutoCallPaused,
    setLoggedIn,
    setSelectedUser,
    startCall,
    updateUser,
    userCallLogs,
    users,
  ]);

  return (
    <AppStoreContext.Provider value={value}>
      {children}
    </AppStoreContext.Provider>
  );
};

export const useAppStore = () => {
  const context = useContext(AppStoreContext);

  if (!context) {
    throw new Error('useAppStore must be used within an AppStoreProvider');
  }

  return context;
};

export default AppStoreContext;
